/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchFilters, Header, DrawerHeader } from 'datastake-daf/dist/components';
import Styles from '../locations/style';

import LIRCreate from './create';
import getColumns from './columns';
import { connect } from 'react-redux';
import { actions } from '../../../store/reducers/lir';
import { displayMessage } from '../../../helpers/Messages';
import { canViewFeatureAction } from '../../../constants/roles';
import { PROJECTS } from '../../../helpers/CanAccess';
import Loading from '../../../components/Loading';
import {
  getLirFilterOptions,
  mapLirs,
  selectFiltersConfig,
  checkboxConfig,
  filtersConfig,
} from './helper';
import { filterParams, useFilters } from '../../../helpers/SubjectSearch';
import { useNavigate } from 'react-router-dom';
import DafTable from '../../Table/newTable';
import { renderBreadCrumbs } from '../../../helpers/breadCrumbs';
import TablePagination from '../../Table/pagination';
import { CREATE_DRAWER_WIDTH } from '../../../helpers/form';

const view = 'lir';

function LIR({
  module = 'pme',
  lir: { data, loading },
  lirGet,
  lirFormReset,
  authentication: { user, selectedProject, sourceId }
}) {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [hasError, setHasError] = useState(false);
  const goTo = useNavigate();
  const [editVisible, setEditVisible] = useState(false);
  const [lirs, setLIRs] = useState([]);
  const [selectOptions, setSelectOptions] = useState({
    country: [],
    category: [],
  });

  const {
    newFiltersConfig,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    setPagination,
    onFiltersChange,
    setActiveFilters,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
  } = useFilters({
    selectFiltersConfig,
    view,
    module,
    filtersConfig,
  });

  const columns = useMemo(() => getColumns(
    t,
    module,
    undefined,
    selectedProject !== PROJECTS.myData ? 'project' : 'show',
    sourceId,
    activeFilters,
  ), [t, module, selectedProject, sourceId, activeFilters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sourceId) {
      lirGet(filterParams({
        sourceId,
        activeFilters,
      }));
    }
  }, [user.language, selectedProject, sourceId, activeFilters])


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data.lirs) {
      setLIRs(mapLirs(data.lirs, data.lirOptions))
    }

    setSelectOptions((prev) => ({
      ...prev,
      ...getLirFilterOptions(data.lirOptions),
    }));

    if (typeof data.totalItems === 'number') {
      setPagination((prev) => ({ ...prev, total: data.totalItems }));
    }
  }, [data]);

  const canClearSearch = useMemo(() => {
    return !!activeFilters.search;
  }, [activeFilters]);

  const canEdit = (canViewFeatureAction({ user, module, feature: 'lir', action: 'create' }) ||
    canViewFeatureAction({ user, module, feature: 'network', subfeature: 'lir', action: 'create' })) && selectedProject === PROJECTS.myData;

  const breadCrumbs = useMemo(() =>
    renderBreadCrumbs({ t, view: 'lir', goTo, mod: module }), [t, goTo]);

  return (
    <Styles className="semibold form-input-output">
      {loading && <Loading />}
      <Header
        title={t("Local Information Relays")}
        breadcrumbs={breadCrumbs}
        actionButtons={canEdit ? [
          { label: t("New"), onClick: () => setEditVisible(true), type: 'primary' }
        ] : []}
      />
      <div className="column start table-content">
        <div className='mt-6 ml-6 mr-6'>
          <SearchFilters
            t={t}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            hasError={hasError}
            setHasError={setHasError}
            filtersConfig={newFiltersConfig}
            checkboxConfig={checkboxConfig}
            onSearch={onSearch}
            activeFilters={activeFilters}
            canClear={canClearSearch}
            clearFilters={() => setActiveFilters({})}
          />
        </div>
        <div className='daf-table-wrapper pagination-w-padding'>
          <DafTable
            columns={columns}
            data={lirs}
            loading={loading}
            pagination={pagination}
            rowKey="dbId"
            selectOptions={selectOptions}
            filtersConfig={selectFiltersConfig}
            onFilterChange={onFiltersChange}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            sourcesKey="authorId"
            defaultFilters={defaultFilters}
            onChange={onTableChange}
          />
          <TablePagination
            t={t}
            page={pagination.current}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={pagination.total}
            doTotalItems
            onChangePagination={(val) => {
              onTableChange({ ...pagination, current: 1, pageSize: val });
            }}
            perPage={pagination.pageSize}
            arrowIcons
          />
        </div>
      </div>
      <Drawer
        destroyOnClose={true}
        title={<DrawerHeader title={t("Create LIR")} />}
        open={editVisible}
        maskClosable={false}
        onClose={() => {
          setEditVisible(false);
          goTo(`/app/${module}/lir`)
          lirGet(filterParams({
            sourceId,
            activeFilters,
          }));
          lirFormReset({ module, view: ['scoping', 'new'] });
        }}
        width={CREATE_DRAWER_WIDTH}
        bodyStyle={{ padding: 0 }}>
        {editVisible ? <LIRCreate
          showSaveAndNext={false}
          namespace="Lir"
          module={module}
          view={["scoping", "new"]}
          edit={false}
          onSubmitted={(type, m, payload) => {
            displayMessage(type, m);
            setEditVisible(false);
            if (payload) {
              goTo(`/app/${module}/edit/lir/${payload.datastakeId}`)
            } else {
              goTo(`/app/${module}/lir`)
            }
            lirGet(filterParams({
              sourceId,
              activeFilters,
            }));
            lirFormReset({ module, view: ['scoping', 'new'] });
          }}
          onCancel={() => setEditVisible(false)} /> : null}
      </Drawer>
    </Styles>
  )
};

const mstp = (state) => ({ lir: state.lir, authentication: state.authentication });

export default connect(mstp, actions)(LIR);
